'use client';

import { useEffect, useRef, useState } from 'react';

import useSWR from 'swr';

import { useFeedSummaryPolling } from '@/hooks/useFeedSummaryPolling';
import type { FeedItem } from '@/lib/api';
import { normalizeThreadNodes, THREAD_NODE_KIND } from '@/lib/api';
import {
  getGetPersonalFeedUrl,
  getGetPublicFeedUrl,
  getGetSocialFeedUrl,
  getPersonalFeed,
  getPublicFeed,
  getSocialFeed,
} from '@/lib/api/generated';
import { API_URL } from '@/lib/consts';

/** Identifies one independently requested feed endpoint. */
export type FeedEndpoint = 'public' | 'personal' | 'social';

/** Configures endpoint-specific visible-item behavior during request failures. */
type FeedSectionOptions = {
  suppressItemsOnError: boolean;
};

/** Exposes one feed endpoint's request, pagination snapshot, and polling state. */
export type FeedSectionState = {
  currentFeedItems: FeedItem[] | undefined;
  error: unknown;
  feedRequestTarget: string;
  hasResolvedCurrentRequest: boolean;
  visibleFeedItems: FeedItem[] | null;
  isLoadingMore: boolean;
  beginLoadMore: (nextCount: number) => void;
};

/** Maps the UI's feed names to their Rust-generated path builders and requests. */
const FEED_OPERATIONS = {
  public: { getUrl: getGetPublicFeedUrl, request: getPublicFeed },
  personal: { getUrl: getGetPersonalFeedUrl, request: getPersonalFeed },
  social: { getUrl: getGetSocialFeedUrl, request: getSocialFeed },
} as const;

/** Keeps only feed cards containing at least one readable document node. */
const renderableFeedItems = (items: FeedItem[] | null): FeedItem[] | null =>
  items?.filter((item) =>
    normalizeThreadNodes(item.thread?.nodes).some((node) => node.kind === THREAD_NODE_KIND.Document)
  ) ?? null;

/** Owns the shared request, pagination snapshot, and polling lifecycle for one feed endpoint. */
export function useFeedSection(
  endpoint: FeedEndpoint,
  numItems: number,
  { suppressItemsOnError }: FeedSectionOptions
): FeedSectionState {
  const operation = FEED_OPERATIONS[endpoint];
  const feedRequestTarget = `${API_URL}${operation.getUrl(numItems)}`;
  const fetchFeed = () => operation.request(numItems);
  const { data: currentFeedItems, error } = useSWR<FeedItem[]>(feedRequestTarget, fetchFeed, {
    revalidateOnFocus: false,
  });
  const [loadMoreSnapshot, setLoadMoreSnapshot] = useState<FeedItem[] | null>(null);
  const [pendingLoadMore, setPendingLoadMore] = useState<{
    sourceTarget: string;
    requestTarget: string;
  } | null>(null);
  const reachedPendingTargetRef = useRef(false);
  const hasResolvedCurrentRequest = currentFeedItems !== undefined || Boolean(error);
  const isLoadingMore = pendingLoadMore?.requestTarget === feedRequestTarget;
  const availableItems = error
    ? suppressItemsOnError
      ? null
      : (currentFeedItems ?? loadMoreSnapshot)
    : currentFeedItems !== undefined
      ? currentFeedItems
      : isLoadingMore
        ? loadMoreSnapshot
        : null;
  const visibleFeedItems = renderableFeedItems(availableItems);

  useEffect(() => {
    if (!pendingLoadMore) {
      return;
    }

    if (feedRequestTarget === pendingLoadMore.requestTarget) {
      reachedPendingTargetRef.current = true;
      if (!hasResolvedCurrentRequest) {
        return;
      }
    } else if (
      feedRequestTarget === pendingLoadMore.sourceTarget &&
      !reachedPendingTargetRef.current
    ) {
      return;
    }

    const preservesFailedPublicSnapshot =
      feedRequestTarget === pendingLoadMore.requestTarget &&
      Boolean(error) &&
      !suppressItemsOnError;
    if (!preservesFailedPublicSnapshot) {
      setLoadMoreSnapshot(null);
    }
    setPendingLoadMore(null);
    reachedPendingTargetRef.current = false;
  }, [error, feedRequestTarget, hasResolvedCurrentRequest, pendingLoadMore, suppressItemsOnError]);

  useFeedSummaryPolling({
    feedItems: suppressItemsOnError && error ? [] : (currentFeedItems ?? []),
    feedRequestTarget,
    enabled: !error && Boolean(currentFeedItems?.length),
  });

  const beginLoadMore = (nextCount: number): void => {
    setLoadMoreSnapshot(suppressItemsOnError && error ? [] : (currentFeedItems ?? []));
    setPendingLoadMore({
      sourceTarget: feedRequestTarget,
      requestTarget: `${API_URL}${operation.getUrl(nextCount)}`,
    });
    reachedPendingTargetRef.current = false;
  };

  return {
    currentFeedItems,
    error,
    feedRequestTarget,
    hasResolvedCurrentRequest,
    visibleFeedItems,
    isLoadingMore,
    beginLoadMore,
  };
}
